import { useEffect, useState, useRef } from 'react';
import { motion, Variants } from 'framer-motion';

interface TypewriterProps {
  text: string | string[];
  speed?: number;
  deleteSpeed?: number;
  waitTime?: number;
  loop?: boolean;
  className?: string;
  cursorChar?: string | React.ReactNode;
  cursorAnimationVariants?: {
    initial: object;
    animate: object;
  };
  cursorClassName?: string;
}

const DEFAULT_CURSOR_VARIANTS: { initial: Variants['initial']; animate: Variants['animate'] } = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.5,
      repeat: Infinity,
      repeatType: 'reverse' as const,
      ease: 'easeInOut',
    },
  },
};

const Typewriter: React.FC<TypewriterProps> = ({
  text,
  speed = 70,
  deleteSpeed = 40,
  waitTime = 1500,
  loop = true,
  className = '',
  cursorChar = '|',
  cursorAnimationVariants = DEFAULT_CURSOR_VARIANTS,
  cursorClassName = '',
}) => {
  const texts = Array.isArray(text) ? text : [text];
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const currentText = texts[currentIndex];

    const tick = () => {
      if (!isDeleting) {
        if (displayText.length < currentText.length) {
          setDisplayText(currentText.slice(0, displayText.length + 1));
          timeoutRef.current = setTimeout(tick, speed);
        } else {
          if (!loop && currentIndex === texts.length - 1) {
            setIsDone(true);
            return;
          }
          timeoutRef.current = setTimeout(() => {
            if (texts.length > 1) setIsDeleting(true);
            else if (loop) setDisplayText('');
          }, waitTime);
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText(currentText.slice(0, displayText.length - 1));
          timeoutRef.current = setTimeout(tick, deleteSpeed);
        } else {
          setIsDeleting(false);
          setCurrentIndex((prev) => (prev + 1) % texts.length);
        }
      }
    };

    timeoutRef.current = setTimeout(tick, isDeleting ? deleteSpeed : speed);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayText, isDeleting, currentIndex]);

  return (
    <span className={className}>
      {displayText}
      {!isDone && (
        <motion.span
          className={cursorClassName}
          initial={cursorAnimationVariants.initial as Variants['initial']}
          animate={cursorAnimationVariants.animate as Variants['animate']}
        >
          {cursorChar}
        </motion.span>
      )}
    </span>
  );
};

export { Typewriter };
